import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Dashboard from './Dashboard';
import Loading from './Loading';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Error display component
const ErrorDisplay = ({ message, onRetry }) => (
  <div className="error-container">
    <div className="error-content">
      <h2>Error Loading Your Data</h2>
      <p>{message}</p>
      <button className="retry-button" onClick={onRetry}>
        Try Again
      </button>
    </div>
  </div>
);

// No data display component
const NoDataDisplay = ({ onRetry }) => (
  <div className="section">
    <div className="container">
      <h2>No Data Available</h2>
      <p>We couldn't find any Spotify data for your account.</p>
      <button className="login-button" onClick={onRetry}>
        Try Again
      </button>
    </div>
  </div>
);

function WrappedExperience() {
  const navigate = useNavigate();
  const location = useLocation();
  const [spotifyData, setSpotifyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');

  // Handle theme changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Fetch Spotify data
  const fetchSpotifyData = useCallback(async () => {
    try {
      const token = localStorage.getItem('spotify_access_token');
      
      if (!token) {
        console.error('No access token found');
        window.location.href = '/';
        return;
      }

      const response = await fetch(`${API_URL}/api/user-data`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('spotify_access_token');
          window.location.href = '/';
          return;
        }
        throw new Error(`Failed to fetch data (${response.status})`);
      }

      const data = await response.json();
      setSpotifyData(data);
      setError(null);
    } catch (error) {
      setError(error.message || 'Failed to fetch Spotify data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle access token in URL and initial data fetch
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accessToken = params.get('access_token');
    
    if (accessToken) {
      localStorage.setItem('spotify_access_token', accessToken);
      navigate('/wrapped', { replace: true });
    }
    
    fetchSpotifyData();
  }, [location, navigate, fetchSpotifyData]);

  // Handle retry
  const handleRetry = () => {
    setLoading(true);
    setError(null);
    fetchSpotifyData();
  };

  if (loading) {
    return <Loading message="Loading your Spotify data..." />;
  }

  if (error) {
    return <ErrorDisplay message={error} onRetry={handleRetry} />;
  }

  if (!spotifyData) {
    return <NoDataDisplay onRetry={() => window.location.href = '/'} />;
  }

  return <Dashboard spotifyData={spotifyData} theme={theme} setTheme={setTheme} />;
}

export default WrappedExperience; 